import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { Subject, takeUntil } from 'rxjs';

export type BotonIconoPosicion = 'izquierda' | 'derecha';
export type BotonType = 'button' | 'submit' | 'reset';

@Component({
    selector: 'app-boton-dinamico',
    templateUrl: './boton-dinamico.component.html',
    styleUrls: ['./boton-dinamico.component.scss'],
})
export class BotonDinamicoComponent implements OnInit, OnDestroy {
    /** Texto visible del botón. */
    @Input() texto: string = '';

    /** Nombre del ícono SVG (e.g. 'heroicons_solid:arrow-left'). Opcional. */
    @Input() icono?: string;

    /** Posición del ícono respecto al texto. Default: 'izquierda'. */
    @Input() iconoPosicion: BotonIconoPosicion = 'izquierda';

    /**
     * Nombre de clase CSS definida en boton-accion.component.scss con @apply.
     * Ejemplos: 'button-back', 'button-default', 'button-full'.
     * También acepta múltiples clases separadas por espacio.
     */
    @Input() clases: string = '';

    /**
     * Color del botón en formato CSS (hex, rgb, etc.).
     * - undefined (no se pasa) → usa el color primario de la empresa.
     * - '' (vacío explícito)  → usa la clase fallback 'bg-primary'.
     * - '#RRGGBB'             → aplica ese color a border y texto.
     */
    @Input() color?: string;

    /** Deshabilita el botón. */
    @Input() disabled: boolean = false;

    /** Muestra un spinner y deshabilita el botón (evita doble envío en formularios). */
    @Input() loading: boolean = false;

    /** Tipo HTML del botón. Default: 'button'. */
    @Input() type: BotonType = 'button';

    @Input() fontIcon?: string;
    @Input() classIcon?: string;
    @Input() hideTextMobile: boolean = false;

    /** Se emite al hacer click sobre el botón. */
    @Output() accion = new EventEmitter<void>();

    private _colorEmpresa: string = '';
    private _unsubscribeAll = new Subject<void>();

    constructor(private _empresaService: EmpresaService) {}

    ngOnInit(): void {
        this._empresaService.empresa$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((empresa) => {
                this._colorEmpresa = empresa.color_primario;
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /** Color resuelto: prioriza el Input `color`, luego el color de empresa. */
    get colorActivo(): string {
        return this.color !== undefined ? this.color : this._colorEmpresa;
    }

    /** Clase fallback cuando no hay color activo. */
    get colorClass(): string {
        return this.colorActivo ? '' : 'bg-primary';
    }

    /** Valor CSS para los estilos de border y color (texto/ícono). */
    get colorStyle(): string {
        return this.colorActivo || '';
    }

    /** Retorna el color de texto/borde garantizando contraste tanto en sólido como en outline. */
    get textColorStyle(): string {
        const isOutline = this.clases ? this.clases.includes('button-outline') : false;
        const hasBgClass = this.clases ? this.clases.includes('bg-') : false;
        
        if (this.colorStyle) {
            const isLight = this.isColorLight(this.colorStyle);
            
            // Si es sólido y el fondo es claro, el texto debe ser oscuro
            if (!isOutline && !hasBgClass) {
                return isLight ? '#0f172a' : '#ffffff';
            }
            
            // Si es outline (fondo claro/blanco) y el color de texto/borde es muy claro, forzar oscuro
            if (isOutline && isLight) {
                return '#0f172a';
            }
        }
        return this.colorStyle;
    }

    /** Determina si un color hexadecimal es claro. */
    isColorLight(hexColor: string): boolean {
        if (!hexColor) return false;
        const hex = hexColor.replace('#', '');
        if (hex.length < 6) return false;
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return yiq >= 128;
    }

    /** Determina el color de contraste dinámico. */
    getContrastColor(hexColor: string): string {
        if (!hexColor) return '#ffffff';
        const hex = hexColor.replace('#', '');
        if (hex.length < 6) return '#ffffff';
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? '#0f172a' : '#ffffff';
    }

    manejarClick(): void {
        this.accion.emit();
    }
}
